import React, { useEffect, useState, useRef } from 'react';
import { MessageSquare, Save, AlignLeft, Type, Trash, List, Plus, X, Edit, Image as ImageIcon, Upload } from 'lucide-react';
import { toast } from 'react-toastify';
import api from '../utils/api';
import Container from '../components/Container';
import FullScreenLoader from '../components/Loader';
import moment from 'moment';

const MessageTemplate = () => {
  const [heading, setHeading] = useState('');
  const [message, setMessage] = useState('');
  const [id, setid] = useState('');
  const [image, setImage] = useState(null); 
  const [imagePreview, setImagePreview] = useState(''); 
  const [loading, setLoading] = useState(false);
  const [templateList, setTemplateList] = useState([]);
  const [showForm, setShowForm] = useState(false);
  
  const fileInputRef = useRef(null);

  useEffect(() => {
    getTemplate();
  }, []);

  const getTemplate = async () => {
    setLoading(true);
    try {
      const response = await api.get('/template/get-template');
      if (response.status === 200) {
        setTemplateList(response.data);
      }
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  
  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setImage(file);
      setImagePreview(URL.createObjectURL(file)); 
    }
  };

  const editTemplate = (data) => {
    if (data) {
      setid(data?.id);
      setMessage(data?.content);
      setHeading(data?.title);
      setImagePreview(data?.image_url || ''); 
      setImage(null);
      setShowForm(true);
    }
  };

  const resetForm = () => {
    setHeading('');
    setMessage('');
    setid('');
    setImage(null);
    setImagePreview('');
    setShowForm(false);
  };

  const handleSaveTemplate = async () => {
    if (!heading || !message) {
      toast.error("Please fill in both Heading and Message fields.");
      return;
    }

    setLoading(true);
    try {
      const formData = new FormData();
      formData.append('title', heading);
      formData.append('content', message);
      if (id) formData.append('id', id);
      if (image) formData.append('image', image);

      let url = id ? '/template/update-template' : '/template/save-template';

      const response = await api.post(url, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      if (response.status === 200 || response.status === 201) {
        toast.success("Template saved successfully!");
        resetForm();
        getTemplate();
      }
    } catch (err) {
      console.error(err);
      toast.error("Failed to save template.");
    } finally {
      setLoading(false);
    }
  };

  const deleteTemplate = async (id) => {
    if (!window.confirm("Are you sure?")) return;
    setLoading(true);
    try {
      const response = await api.get('/template/delete-template/' + id);
      if (response.status === 200) {
        toast.success("Template Deleted Successfully");
        getTemplate();
      }
    } catch (err) {
      toast.error("Failed to delete");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container 
      title={'Message Templates'} 
      rightSection={
        <button 
          onClick={() => (showForm ? resetForm() : setShowForm(true))} 
          style={showForm ? styles.btnClose : styles.btnAdd}
        >
          {showForm ? <><X size={16} /> Close</> : <><Plus size={16} /> Add Template</>}
        </button>
      }
    >
      {loading && <FullScreenLoader />}

      <div style={styles.mainWrapper}>
        {showForm && (
          <div style={styles.formSection}>
            <div style={styles.card}>
              <div style={styles.cardHeader}>
                <MessageSquare size={18} color="#4f46e5" />
                <span style={styles.cardTitle}>{id ? 'Edit Template' : 'New Template Details'}</span>
              </div>

              <div style={styles.formGrid}>
                {/* Left Column: Inputs */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
                  <div style={styles.inputGroup}>
                    <label style={styles.label}><Type size={14} /> Template Heading</label>
                    <input
                      type="text"
                      style={styles.textInput}
                      value={heading}
                      onChange={(e) => setHeading(e.target.value)}
                    />
                  </div>

                  <div style={styles.inputGroup}>
                    <label style={styles.label}><ImageIcon size={14} /> Attachment (Image)</label>
                    <div 
                      style={styles.imageUploadBox} 
                      onClick={() => fileInputRef.current.click()}
                    >
                      {imagePreview ? (
                        <img src={imagePreview} alt="Preview" style={styles.previewImg} />
                      ) : (
                        <div style={styles.uploadPlaceholder}>
                          <Upload size={24} />
                          <span>Click to upload image</span>
                        </div>
                      )}
                      <input 
                        type="file" 
                        ref={fileInputRef} 
                        onChange={handleImageChange} 
                        accept="image/*" 
                        style={{ display: 'none' }} 
                      />
                    </div>
                  </div>
                </div>

                {/* Right Column: Textarea */}
                <div style={styles.inputGroup}>
                  <label style={styles.label}><AlignLeft size={14} /> Message Content</label>
                  <textarea
                    style={{ ...styles.textArea, height: '100%' }}
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                  />
                </div>
              </div>

              <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '20px' }}>
                <button
                  style={(heading && message) ? styles.btnSaveActive : styles.btnSaveDisabled}
                  disabled={!heading || !message}
                  onClick={handleSaveTemplate}
                >
                  <Save size={18} /> {id ? 'Update Template' : 'Save Template'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Listing Section */}
        <div style={styles.listSection}>
          <div style={styles.listHeaderIcon}>
            <List size={18} color="#4f46e5" />
            <span style={styles.cardTitle}>Saved Templates ({templateList.length})</span>
          </div>
          
          <div style={tableWrapper}>
            <table style={tableStyle}>
              <thead>
                <tr style={thRow}>
                  <th style={thStyle}>S No.</th>
                  <th style={thStyle}>Preview</th>
                  <th style={thStyle}>Title</th>
                  <th style={thStyle}>Message</th>
                  <th style={thStyle}>Action</th>
                </tr>
              </thead>
              <tbody>
                {templateList.map((item, index) => (
                  <tr key={index} style={trStyle}>
                    <td style={tdStyle}>{index + 1}</td>
                    <td style={tdStyle}>
                      {item.image_url ? (
                        <img src={item.image_url} alt="thumb" style={styles.thumbImg} />
                      ) : (
                        <span style={{color: '#ccc'}}>No Image</span>
                      )}
                    </td>
                    <td style={tdStyle}><strong>{item.title}</strong></td>
                    <td style={tdStyle}><div style={styles.msgColumn}>{item.content}</div></td>
                    <td style={tdStyle}>
                      <button style={styles.editBtn} onClick={() => editTemplate(item)}><Edit size={16} /></button>
                      <button style={styles.deleteBtn} onClick={() => deleteTemplate(item.id)}><Trash size={16} /></button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </Container>
  );
};


const styles = {
  
  mainWrapper: { display: 'flex', flexDirection: 'column', gap: '25px', marginTop: '10px' },
  btnAdd: { backgroundColor: '#4f46e5', color: 'white', border: 'none', padding: '8px 16px', borderRadius: '8px', fontWeight: '600', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '8px' },
  btnClose: { backgroundColor: '#f1f5f9', color: '#64748b', border: '1px solid #e2e8f0', padding: '8px 16px', borderRadius: '8px', fontWeight: '600', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '8px' },
  formSection: { width: '100%' },
  card: { backgroundColor: 'white', padding: '20px', borderRadius: '15px', border: '1px solid #e2e8f0', boxShadow: '0 4px 12px rgba(0,0,0,0.05)' },
  cardHeader: { display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '20px' },
  cardTitle: { fontWeight: '700', fontSize: '16px', color: '#1e293b' },
  formGrid: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' },
  inputGroup: { marginBottom: '10px' },
  label: { display: 'flex', alignItems: 'center', gap: '5px', fontSize: '13px', fontWeight: '700', color: '#64748b', marginBottom: '8px' },
  textInput: { width: '100%', boxSizing: 'border-box', padding: '12px', borderRadius: '10px', border: '1px solid #e2e8f0', backgroundColor: '#f8fafc' },
  textArea: { width: '100%', boxSizing: 'border-box', padding: '12px', borderRadius: '10px', border: '1px solid #e2e8f0', backgroundColor: '#f8fafc', resize: 'none' },
  
  
  imageUploadBox: { 
    width: '100%', 
    height: '120px', 
    border: '2px dashed #e2e8f0', 
    borderRadius: '10px', 
    display: 'flex', 
    justifyContent: 'center', 
    alignItems: 'center', 
    cursor: 'pointer',
    overflow: 'hidden',
    backgroundColor: '#f8fafc'
  },
  uploadPlaceholder: { display: 'flex', flexDirection: 'column', alignItems: 'center', color: '#94a3b8', fontSize: '12px', gap: '5px' },
  previewImg: { width: '100%', height: '100%', objectFit: 'cover' },
  thumbImg: { width: '40px', height: '40px', borderRadius: '6px', objectFit: 'cover', border: '1px solid #e2e8f0' },
  
  btnSaveActive: { backgroundColor: '#4f46e5', color: 'white', padding: '10px 25px', borderRadius: '10px', border: 'none', fontWeight: '700', cursor: 'pointer', display: 'flex', gap: '8px' },
  btnSaveDisabled: { backgroundColor: '#e2e8f0', color: '#94a3b8', padding: '10px 25px', borderRadius: '10px', border: 'none', fontWeight: '700', cursor: 'not-allowed', display: 'flex', gap: '8px' },
  listSection: { width: '100%' },
  listHeaderIcon: { display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '15px' },
  msgColumn: { maxWidth: '250px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  deleteBtn: { padding: '6px 10px', backgroundColor: '#ef4444', color: '#fff', border: 'none', borderRadius: '4px', cursor: 'pointer' },
  editBtn: { padding: '6px 10px', backgroundColor: '#33acf3', color: '#fff', border: 'none', borderRadius: '4px', cursor: 'pointer', marginRight: 10 }
};


const tableWrapper = { backgroundColor: '#fff', borderRadius: '12px', border: '1px solid #e2e8f0', overflow: 'hidden' };
const tableStyle = { width: '100%', borderCollapse: 'collapse' };
const thRow = { backgroundColor: '#f8fafc' };
const thStyle = { padding: '15px', textAlign: 'left', borderBottom: '2px solid #f1f5f9', color: '#64748b', fontSize: '12px', fontWeight: '800', textTransform: 'uppercase' };
const tdStyle = { padding: '15px', borderBottom: '1px solid #f1f5f9', fontSize: '14px' };
const trStyle = { transition: '0.2s' };

export default MessageTemplate;